import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  ContributionDetailBody,
  type ContributionDetail,
} from '@/components/giving/contribution-detail-body';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate } from '@/utils/format';

export const ContributionDetailDialog = ({
  contributionId,
  open,
  onOpenChange,
}: {
  contributionId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const locale = usePreferences((state) => state.locale);

  const detail = useQuery({
    queryKey: ['giving', 'contributions', contributionId],
    queryFn: () => api.get<ContributionDetail>(`/giving/contributions/${contributionId}`),
    enabled: open && Boolean(contributionId),
  });

  const giftDate = detail.data?.contributionDate
    ? formatDate(detail.data.contributionDate, { locale, style: 'medium' })
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" className="overflow-visible" closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>
            {giftDate
              ? t('contributions.detail.dialogTitleDated', { date: giftDate })
              : t('contributions.detail.dialogTitle')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {contributionId ? (
            <ContributionDetailBody contributionId={contributionId} linkOut={false} />
          ) : null}
        </DialogBody>
        <DialogFooter className="justify-end">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {tCommon('action.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
